import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { Link } from 'react-router-dom';
import '../assets/css/Packages.css';
import { getPackages } from '../services/packageService';

const Packages = () => {
    const cardsRef = useRef([]);
    const [packages, setPackages] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const data = await getPackages();
                
                // Map backend data to frontend structure
                const mappedPackages = data.map((pkg, index) => {
                    const cssClasses = ['card-design', 'card-dev', 'card-webflow'];
                    return {
                        id: pkg.id,
                        title: pkg.title,
                        cssClass: cssClasses[index % cssClasses.length],
                        // Handle is_most_popular: backend returns ucfirst string like "Yes" or "No"
                        is_most_popular: pkg.is_most_popular && pkg.is_most_popular.toLowerCase() === 'yes',
                        plan_icon: pkg.plan_icon,
                        currency_icon: pkg.currency_icon,
                        price: pkg.amount, // Backend sends 'amount'
                        period: pkg.period,
                        description: pkg.description || "", // Handle missing description
                        button_text: pkg.button_name,
                        button_link: pkg.button_link || "/contact-us",
                        status: pkg.status
                    };
                });

                setPackages(mappedPackages);
            } catch (error) {
                console.error("Error fetching packages:", error);
                const errorMessage = error.response 
                    ? `Server Error: ${error.response.status} - ${error.response.statusText}`
                    : error.message || "Failed to load packages. Please check your connection.";
                setError(errorMessage);
            }
        };

        fetchPackages();
    }, []);

    useLayoutEffect(() => {
        if (packages.length > 0) {
            // Reset refs to ensure we only animate valid elements
            cardsRef.current = cardsRef.current.slice(0, packages.length);
            
            gsap.fromTo(cardsRef.current,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 0.8, stagger: 0.2, ease: "power3.out", clearProps: "transform" }
            );
        }
    }, [packages]);

    const addToRefs = (el) => {
        if (el && !cardsRef.current.includes(el)) {
            cardsRef.current.push(el);
        }
    };

    const renderBusAnimation = () => (
        <div className="bus-animation-container">
            <div className="bus-wrapper">
                <div className="bus-body">
                    <div className="bus-window"></div>
                    <div className="bus-stripe"></div>
                </div>
                <div className="bus-wheel wheel-front"></div>
                <div className="bus-wheel wheel-back"></div>
            </div>
        </div>
    );

    return (
        <div className="packages-page">
            {/* Page Header */}
            <section id="main-banner-page" className="position-relative page-header contact-header section-nav-smooth parallax" style={{ zIndex: 5 }}>
                <div className="overlay overlay-dark opacity-6"></div>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 offset-lg-3">
                            <div className="page-titles whitecolor text-center padding">
                                <h2 className="font-bold"> Packages</h2>
                                <h3 className="font-light py-2">Pricing Plans</h3>
                            </div>
                        </div>
                    </div>
                    <div className="gradient-bg title-wrap mt-n5">
                        <div className="row">
                            <div className="col-lg-12 col-md-12 whitecolor">
                                <h3 className="float-left">Packages</h3>
                                <ul className="breadcrumb top10 bottom10 float-right">
                                    <li className="breadcrumb-item hover-light"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item hover-light">Packages</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Page Header ends */}

            {/* Main Content Section */}
            <section className="packages-content">
                {error ? (
                    <div className="text-center text-danger padding">
                        <h3>{error}</h3>
                    </div>
                ) : packages.length === 0 ? (
                    <div className="text-center padding">
                        <h3>No packages available at the moment.</h3>
                    </div>
                ) : (
                    /* Pricing Table (3 cols) */
                    <div className="pricing-table">
                        {packages.map((pkg) => (
                            <div className={`pricing-card ${pkg.cssClass}`} ref={addToRefs} key={pkg.id}>
                                {renderBusAnimation()}
                                
                                {pkg.is_most_popular && <div className="most-popular-badge">Most Popular</div>}
                                
                                <div className="card-header">
                                    <h3>{pkg.title}</h3>
                                    <div className="star-icon rotating-star">
                                        <i className={pkg.plan_icon || "fas fa-star"}></i>
                                        {/* Small decorative circle for Basic Plan (id 1) */}
                                        {pkg.id === 1 && <div className="decorative-round decorative-round-2"></div>}
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="price">
                                        <span className="currency-icon" style={{ fontSize: 'inherit', color: 'inherit', marginRight: '5px' }}>
                                            {pkg.currency_icon && pkg.currency_icon.includes('fa') ? (
                                                <i className={pkg.currency_icon}></i>
                                            ) : (
                                                pkg.currency_icon || "₹"
                                            )}
                                        </span>
                                        {pkg.price}<span> / {pkg.period}</span>
                                    </div>
                                    <div 
                                        className="card-description" 
                                        style={{ color: 'black' }}
                                        dangerouslySetInnerHTML={{ __html: pkg.description }}
                                    ></div>
                                    <div className="card-footer">
                                        <div className="button-wrapper">
                                            <div className="decorative-round decorative-round-3"></div>
                                            <Link to={pkg.button_link} className="btn-pricing">{pkg.button_text}</Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Packages;

